import {
  AUDIO_RECORDING_MAX_MS,
  FLUTTER_COMPAT_AUDIO_EXTENSION,
  FLUTTER_COMPAT_AUDIO_MIME,
} from './audio';
import { ffmpegCoreUrl, ffmpegWasmUrl } from '@shared/config/runtime';

const INPUT_FILE_NAME = 'input-audio';
const OUTPUT_FILE_NAME = `output-audio.${FLUTTER_COMPAT_AUDIO_EXTENSION}`;
const PLAYBACK_OUTPUT_FILE_NAME = 'playback-audio.wav';
const FFMPEG_UMD_URL = '/vendor/ffmpeg/ffmpeg.js';

function resolveRuntimeAssetUrl(path: string) {
  if (typeof window === 'undefined') {
    return path;
  }
  return new URL(path, window.location.href).toString();
}

let ffmpegLoadPromise: Promise<any> | null = null;
let ffmpegJobQueue = Promise.resolve();

declare global {
  interface Window {
    FFmpegWASM?: {
      FFmpeg: new () => {
        load: (config?: {
          coreURL?: string;
          wasmURL?: string;
          workerURL?: string;
        }) => Promise<boolean>;
        exec: (args: string[], timeout?: number) => Promise<number>;
        writeFile: (path: string, data: Uint8Array | string) => Promise<boolean>;
        readFile: (path: string, encoding?: string) => Promise<Uint8Array | string>;
        deleteFile: (path: string) => Promise<boolean>;
      };
    };
  }
}

let ffmpegScriptPromise: Promise<void> | null = null;

async function runQueued<T>(job: () => Promise<T>) {
  const nextJob = ffmpegJobQueue.then(job, job);
  ffmpegJobQueue = nextJob.then(
    () => undefined,
    () => undefined,
  );
  return nextJob;
}

async function loadFfmpegScript() {
  if (typeof window === 'undefined') {
    throw new Error('FFmpeg 仅支持在浏览器环境中使用');
  }
  if (window.FFmpegWASM?.FFmpeg) {
    return;
  }
  if (!ffmpegScriptPromise) {
    ffmpegScriptPromise = new Promise<void>((resolve, reject) => {
      const existing = document.querySelector<HTMLScriptElement>('script[data-ffmpeg-umd="true"]');
      if (existing) {
        existing.addEventListener('load', () => resolve(), { once: true });
        existing.addEventListener('error', () => reject(new Error('FFmpeg UMD 脚本加载失败')), { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = resolveRuntimeAssetUrl(FFMPEG_UMD_URL);
      script.async = true;
      script.dataset.ffmpegUmd = 'true';
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('FFmpeg UMD 脚本加载失败'));
      document.head.appendChild(script);
    });
  }

  await ffmpegScriptPromise;
  if (!window.FFmpegWASM?.FFmpeg) {
    throw new Error('FFmpeg UMD 脚本已加载，但未找到 FFmpeg 导出');
  }
}

async function getFfmpeg() {
  if (!ffmpegLoadPromise) {
    ffmpegLoadPromise = (async () => {
      await loadFfmpegScript();
      const FFmpeg = window.FFmpegWASM?.FFmpeg;
      if (!FFmpeg) {
        throw new Error('FFmpeg 初始化失败');
      }
      const ffmpeg = new FFmpeg();
      await ffmpeg.load({
        coreURL: resolveRuntimeAssetUrl(ffmpegCoreUrl),
        wasmURL: resolveRuntimeAssetUrl(ffmpegWasmUrl),
      });
      return ffmpeg;
    })();
  }

  return ffmpegLoadPromise.catch((error) => {
    resetFfmpegInstance();
    throw error;
  });
}

export async function preloadAudioTranscoder() {
  await getFfmpeg();
}

function resetFfmpegInstance() {
  ffmpegLoadPromise = null;
}

function cloneUint8Array(data: Uint8Array) {
  return new Uint8Array(data.slice().buffer);
}

export async function transcodeRecordedAudioToFlutterOpus(blob: Blob, inputMimeType: string) {
  return runQueued(async () => {
    const ffmpeg = await getFfmpeg();
    const inputExtension = inputMimeType.includes('ogg')
      ? 'ogg'
      : inputMimeType.includes('webm')
        ? 'webm'
        : 'bin';
    const inputPath = `${INPUT_FILE_NAME}.${inputExtension}`;

    await ffmpeg.writeFile(inputPath, new Uint8Array(await blob.arrayBuffer()));

    try {
      const exitCode = await ffmpeg.exec([
        '-i',
        inputPath,
        '-vn',
        '-ar',
        '16000',
        '-ac',
        '1',
        '-c:a',
        'libopus',
        '-b:a',
        '24k',
        '-f',
        FLUTTER_COMPAT_AUDIO_EXTENSION,
        OUTPUT_FILE_NAME,
      ], AUDIO_RECORDING_MAX_MS);

      if (exitCode !== 0) {
        throw new Error(`FFmpeg exited with code ${exitCode}`);
      }

      const output = await ffmpeg.readFile(OUTPUT_FILE_NAME);
      if (!(output instanceof Uint8Array) || output.length === 0) {
        throw new Error('FFmpeg did not produce an audio payload');
      }
      return {
        bytes: output,
        mimeType: FLUTTER_COMPAT_AUDIO_MIME,
      };
    } catch (error) {
      resetFfmpegInstance();
      throw error;
    } finally {
      await Promise.allSettled([
        ffmpeg.deleteFile(inputPath),
        ffmpeg.deleteFile(OUTPUT_FILE_NAME),
      ]);
    }
  });
}

export async function transcodeFlutterAudioToPlayableWav(audioBytes: Uint8Array) {
  return runQueued(async () => {
    const ffmpeg = await getFfmpeg();
    const inputPath = `${INPUT_FILE_NAME}.${FLUTTER_COMPAT_AUDIO_EXTENSION}`;
    await ffmpeg.writeFile(inputPath, cloneUint8Array(audioBytes));

    try {
      const exitCode = await ffmpeg.exec([
        '-i',
        inputPath,
        '-vn',
        '-acodec',
        'pcm_s16le',
        '-ar',
        '16000',
        '-ac',
        '1',
        PLAYBACK_OUTPUT_FILE_NAME,
      ], AUDIO_RECORDING_MAX_MS);

      if (exitCode !== 0) {
        throw new Error(`FFmpeg exited with code ${exitCode}`);
      }

      const output = await ffmpeg.readFile(PLAYBACK_OUTPUT_FILE_NAME);
      if (!(output instanceof Uint8Array) || output.length === 0) {
        throw new Error('FFmpeg did not produce a playable wav payload');
      }

      return {
        bytes: output,
        mimeType: 'audio/wav',
      };
    } catch (error) {
      resetFfmpegInstance();
      throw error;
    } finally {
      await Promise.allSettled([
        ffmpeg.deleteFile(inputPath),
        ffmpeg.deleteFile(PLAYBACK_OUTPUT_FILE_NAME),
      ]);
    }
  });
}
